import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityTabInfo } from '@shared/models/entity-display.model';
import { IOption } from '@shared/models/icommons.model';
import { IUserSettings } from '@shared/models/iuser-settings.model';
import { HttpMethods } from '@cbre/http';
import { Observable, catchError, map, shareReplay, tap, throwError } from 'rxjs';
import { AppStorageService, USER_SETTINGS } from './app-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserSharedService extends HttpMethods {
  // Memoizes the response per user so repeated subtab navigation does not re-issue
  // GET /users/:id/settings. Cleared by clearUserSettingsCache on write (HV-05).
  #settings = new Map<number, Observable<IUserSettings>>();

  constructor(
    http: HttpClient,
    private appStorageService: AppStorageService
  ) {
    super(http);
  }

  public getUserSettings(userId: number): Observable<IUserSettings> {
    const cached = this.#settings.get(userId);
    if (cached) return cached;

    const uri = `/users/${userId}/settings`;
    let settings$: Observable<IUserSettings>;
    settings$ = this.httpGet(uri).pipe(
      tap(userSettings => {
        if (this.#settings.get(userId) === settings$) {
          // NOTE: the user settings must be stored as { userSettings },
          // as many places uses it with this structure. To change it, we need
          // to modify some utility functions
          this.appStorageService.setItem(USER_SETTINGS, { userSettings });
        }
      }),
      catchError(err => {
        if (this.#settings.get(userId) === settings$) {
          this.#settings.delete(userId);
        }
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      // Return a copy per subscriber so callers cannot mutate the memoized settings.
      map(settings => JSON.parse(JSON.stringify(settings)) as IUserSettings)
    );
    this.#settings.set(userId, settings$);
    return settings$;
  }

  /** Invalidates the memoized settings so the next getUserSettings hits the network. */
  public clearUserSettingsCache(userId?: number): void {
    if (userId == null) {
      this.#settings.clear();
    } else {
      this.#settings.delete(userId);
    }
  }

  public markets(userId: number, entity?: string, allOption: boolean = true): Observable<IOption[]> {
    let params;
    if (!!entity) {
      params = new HttpParams({ fromObject: { entity } });
    }
    const uri = `/users/${userId}/geo/markets?all=${allOption}`;
    return this.httpPost(uri, [], { params });
  }

  public submarkets(userId: number, markets, entity?: string, type?: number[]): Observable<IOption[]> {
    let paramsObj = {};
    let params: HttpParams;
    const uri = `/users/${userId}/geo/submarkets`;
    paramsObj = entity ? { entity } : paramsObj;
    paramsObj = type && type.length > 0 ? { ...paramsObj, type: type.join(',') } : paramsObj;
    params = new HttpParams({ fromObject: paramsObj });
    return this.httpPost(uri, markets, { params });
  }

  public getGlobalRegion(userId: number): Observable<IOption[]> {
    const uri = `/users/${userId}/geo/globalregions`;
    return this.httpPost(uri, {});
  }

  public getCountries(userId: number, globalregions: number[]): Observable<IOption[]> {
    const uri = `/users/${userId}/geo/countries`;
    return this.httpPost(uri, globalregions);
  }

  public getRegions(userId: number, countries: number[]): Observable<IOption[]> {
    const uri = `/users/${userId}/geo/regions`;
    return this.httpPost(uri, countries);
  }

  public getDistricts(userId: number, submarkets: number[], entity?: string): Observable<IOption[]> {
    let params;
    if (!!entity) {
      params = new HttpParams({ fromObject: { entity } });
    }
    const uri = `/users/${userId}/geo/districts`;
    return this.httpPost(uri, submarkets, { params });
  }

  public getNeighborhoods(userId: number, submarkets: number[], entity?: string): Observable<IOption[]> {
    let params;
    if (!!entity) {
      params = new HttpParams({ fromObject: { entity } });
    }
    const uri = `/users/${userId}/geo/neighborhoods`;
    return this.httpPost(uri, submarkets, { params });
  }

  public getPropertyUsageTypes(userId: number, markets: number[] = null, entity?: string): Observable<IOption[]> {
    const uri = `/users/${userId}/geo/usages`;
    let params;
    if (!!entity) {
      params = new HttpParams({ fromObject: { entity } });
    }
    return this.httpPost(uri, markets, { params });
  }

  /**
   * @deprecated in favor CatalogService
   */
  public getDefaultColumns(entity: string): Observable<any> {
    const uri = `/lookup/columns/default?query=${entity}`;
    return this.httpGet(uri);
  }

  public getUserEntities(userCountrySkey, userLobSkey): Observable<EntityTabInfo[]> {
    const uri = `/catalog/displayoption?country=${userCountrySkey}&lobId=${userLobSkey}`;
    return this.httpGet(uri);
  }
}
